import * as React from 'react'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Image from 'components/Common/Image/Image'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import LoyaltyInfiniteLogo from 'loyalty/components/LoyaltyInfiniteLogo'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import Caption from 'LuxKit/Typography/Caption'

const HAMBURGER_MENU_LUX_PLUS_IMAGE_ID = 'hamburguer-menu-luxplus'
const HAMBURGER_MENU_SOCIETE_IMAGE_ID = 'hamburguer-menu-societe'
const HAMBURGER_MENU_INFINITE_IMAGE_ID = 'infinite-welcome-step-1-5b3127df4dbe'

/**
 * ClickableListItem applies a neutral hover background; restate product surfaces on :hover so rows stay static (no extra hover treatment).
 */
const LuxPlusListItem = styled(ClickableListItem)`
  padding: ${rem(8)};
  min-height: ${rem(50)};
  background-color: ${props => props.theme.palette.product.luxPlus.background};
  color: ${props => props.theme.palette.product.luxPlus.contrast};
  border-radius: ${props => props.theme.borderRadius.L};

  &:not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.product.luxPlus.background};
      }
    }
  }
`

const LoyaltyListItem = styled(ClickableListItem)`
  padding: ${rem(8)};
  min-height: ${rem(50)};
  border-radius: ${props => props.theme.borderRadius.L};
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}

  &:not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        ${tierGradientBackgroundStyle}
      }
    }
  }
`

const LoyaltyImageContainer = styled.div`
  width: ${rem(48)};
  height: ${rem(48)};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
`

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(4)};
`

const SocieteWordmark = styled(LoyaltyProgramNameLogo)`
  display: block;
  height: ${rem(14)};
  width: auto;
`

const InfiniteWordmark = styled(LoyaltyInfiniteLogo)`
  display: block;
  height: ${rem(12)};
  width: auto;
`

interface Props {
  isNew?: boolean
  variant: 'luxPlus' | 'loyalty'
  onClick?: React.MouseEventHandler<HTMLButtonElement>
}

function NavBarHamburgerMenuLoyaltyItem(props: Props) {
  const { variant, onClick } = props
  const intl = useIntl()

  const isLuxPlusMember = useAppSelector(state => getCanRedeemLuxPlusBenefits(state))
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const isInfiniteTier = tier === 'infinite'

  const luxPlusJoinText = intl.formatMessage({
    id: 'riiInfd4OuMemvufgOfd',
    defaultMessage: 'Join our VIP Travel Club',
  })

  const luxPlusMemberText = intl.formatMessage({
    id: 'fasgrSyV_71UXvEDjPuU',
    defaultMessage: 'VIP Travel Club',
  })

  const societeSubtitleText = intl.formatMessage({
    id: 'H0WRhiklV-1In9MhIkvu',
    defaultMessage: 'Earn points and status',
  })

  const infiniteSubtitleText = intl.formatMessage({
    id: 'bF6hESHaZ-rE8HYHXt7j',
    defaultMessage: 'Invite-only membership',
  })

  const subtitleText = isInfiniteTier ? infiniteSubtitleText : societeSubtitleText

  const loyaltyImage = (
    <LoyaltyImageContainer>
      <Image
        id={isInfiniteTier ? HAMBURGER_MENU_INFINITE_IMAGE_ID : HAMBURGER_MENU_SOCIETE_IMAGE_ID}
        dpr={2}
        fit="center"
        objectFit="cover"
      />
    </LoyaltyImageContainer>
  )

  if (variant === 'luxPlus') {
    return (
      <LuxPlusListItem
        size="medium"
        startIcon={<LoyaltyImageContainer>
          <Image
            id={HAMBURGER_MENU_LUX_PLUS_IMAGE_ID}
            dpr={2}
            fit="center"
            objectFit="cover"
          />
        </LoyaltyImageContainer>}
        onClick={onClick}
        to="/lux-plus"
        titleLineClamp={0}
        endIcon={<LineAngleRightBIcon />}
        title={
          <div>
            <LuxPlusLogo colour="inherit" height={12} />
            <Caption variant="medium">{!isLuxPlusMember ? luxPlusJoinText : luxPlusMemberText}</Caption>
          </div>
        }
      />
    )
  }

  return (
    <ProductPaletteProvider palette={`tier-${tier}`}>
      <LoyaltyListItem
        className={`tier-${tier}`}
        size="medium"
        startIcon={loyaltyImage}
        onClick={onClick}
        to={`/${LOYALTY_LANDING_PAGE}`}
        titleLineClamp={0}
        endIcon={<LineAngleRightBIcon />}
        title={
          <TitleGroup>
            {!isInfiniteTier && <SocieteWordmark />}
            {isInfiniteTier && <InfiniteWordmark variant="gradient" />}
            <Caption variant="medium">{subtitleText}</Caption>
          </TitleGroup>
        }
      />
    </ProductPaletteProvider>
  )
}

export default NavBarHamburgerMenuLoyaltyItem
